import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';

import {useTaskInstance} from 'app/tasks/api';
import {extractTaskInstanceId} from 'app/tasks/utils';

import {granularAssigneesToRawAssignees} from '../BaseAssignTaskModal/AssignForm/assignees-to-raw';

import type {
  AllAssignmentsType,
  GranularTaskAssignmentsType,
} from '../BaseAssignTaskModal/AssignForm';
import type {
  SlimTaskInstanceSerializedType,
  TaskAssignmentSerializedType,
  TaskInstanceSerializedType,
  TaskTemplateSerializedType,
  WriteTaskReassignSerializedType,
  WriteTaskTemplateSerializedType,
} from 'sweet-types';

interface Props {
  taskInstance?: SlimTaskInstanceSerializedType | TaskInstanceSerializedType;
  taskTemplate?: TaskTemplateSerializedType;
  children: (baseTask: ExtractReassignableTaskDataType) => React.ReactElement;
}

/**
 * guard component which will ensure all data for task-reassigning is available
 * i.e load taskinstance when only slim-taskinstance is available
 * */
export function LoadTaskForReassignModal({
  taskInstance,
  taskTemplate,
  children,
}: Props) {
  if (taskInstance != null && taskTemplate != null) {
    throw Error(
      'ReassignTaskModal received both taskInstance and taskTemplate props'
    );
  }

  if (taskInstance == null && taskTemplate == null) {
    throw Error('ReassignTaskModal need taskInstance or taskTemplate prop');
  }

  // if taskInstance is a slim tasknstance, fetch the fat one
  const {data: freshInstance} = useTaskInstance(
    taskInstance != null ? extractTaskInstanceId(taskInstance) : skipToken
  );

  if (taskTemplate != null) {
    return children(convertTemplateToReassignable(taskTemplate));
  }

  if (freshInstance != null) {
    return children(convertInstanceToReassignable(freshInstance));
  }

  // fake it till you make it, just continue without assignment info until it appears.
  return children(
    convertInstanceToReassignable({
      assignments: [],
      steps: [],
      id: 0,
      version: {},
      assigned_version_content: null,
      form_fields: [],
      ...taskInstance,
    } as TaskInstanceSerializedType)
  );
}

interface ExtractReassignableTaskDataType {
  task: WriteTaskReassignSerializedType;
  assignedDoc: Pick<
    WriteTaskTemplateSerializedType,
    'content_type' | 'object_id'
  >;
  assignments: AllAssignmentsType;
}

const convertInstanceToReassignable = (
  task: TaskInstanceSerializedType
): ExtractReassignableTaskDataType => {
  const {
    template: {
      url,
      rrule,
      users,
      force_dtstart = false,
      teams,
      roles,
      message,
      assigned_object: {content_type, id: object_id},
    },
    due,
    dtstart,
    description,
    assignments,
  } = task;

  return {
    task: {
      task: url,
      rrule,
      due,
      dtstart,
      force_dtstart,
      description,
      permission: 'view',
      message: message != '' ? message : undefined,
      user_ids: users.map((u) => u.url),
      team_ids: teams.map((u) => u.url),
      role_ids: roles.map((u) => u.url),
      assignment_ids: granularAssigneesToRawAssignees(assignments),
    },
    assignedDoc: {
      content_type,
      object_id,
    },
    assignments: {
      global: {users, teams, roles},
      granular: formatGrnularAssignments(assignments),
    },
  };
};

const convertTemplateToReassignable = (
  task: TaskTemplateSerializedType
): ExtractReassignableTaskDataType => {
  const {
    due,
    dtstart,
    description,
    rrule,
    users,
    force_dtstart,
    teams,
    roles,
    message,
    permission,
    url,
    assigned_object: {content_type, id: object_id},
    assignments,
  } = task;
  return {
    task: {
      task: url,
      message: message != '' ? message : undefined,
      rrule,
      due,
      dtstart,
      force_dtstart,
      description,
      permission,
      user_ids: users.map((u) => u.url),
      team_ids: teams.map((u) => u.url),
      role_ids: roles.map((u) => u.url),
      assignment_ids: granularAssigneesToRawAssignees(assignments),
    },
    assignedDoc: {content_type, object_id},
    assignments: {
      global: {users, teams, roles},
      granular: formatGrnularAssignments(assignments),
    },
  };
};

function formatGrnularAssignments(
  assignments: TaskAssignmentSerializedType[]
): GranularTaskAssignmentsType {
  return assignments.map((a) => ({
    ...a,
    lane: a.lane?.hashid,
  }));
}
